/**
 * Drives a segmented roster import (POST /whitelist/import/commit, then /continue until `done`).
 * The Worker writes one ≤200-row shard per call; each response carries the batch's running totals
 * and only that shard's skipped rows, so the client accumulates the skipped rows itself.
 */
import type { ImportShardResult, ImportSkippedRow } from '@/types';

/** Merge one shard's skipped rows into what is already known (by row index; file order). */
export function accumulateSkipped(prev: readonly ImportSkippedRow[], next: readonly ImportSkippedRow[]): ImportSkippedRow[] {
  const byIndex = new Map(prev.map((r) => [r.index, r]));
  for (const r of next) byIndex.set(r.index, r);
  return [...byIndex.values()].sort((a, b) => a.index - b.index);
}

export interface ImportProgress {
  last: ImportShardResult;
  skipped: ImportSkippedRow[];
}

/**
 * Calls `first` (commit, or a first continue when resuming), then `next(batch_id)` until done.
 * `onProgress` sees every shard. A failure propagates with the progress made so far attached, so the
 * caller can offer 「繼續匯入」 for the same batch.
 */
export async function runImport(
  first: () => Promise<ImportShardResult>,
  next: (batchId: string) => Promise<ImportShardResult>,
  onProgress: (p: ImportProgress) => void,
  skippedSoFar: readonly ImportSkippedRow[] = [],
): Promise<ImportProgress> {
  let last = await first();
  let skipped = accumulateSkipped(skippedSoFar, last.skipped_rows);
  onProgress({ last, skipped });
  while (!last.done) {
    last = await next(last.batch_id);
    skipped = accumulateSkipped(skipped, last.skipped_rows);
    onProgress({ last, skipped });
  }
  return { last, skipped };
}

/** 第 x / y 段 for a shard response (x is the shard just written, 1-based). */
export const shardLabel = (r: { shard: number; shard_count: number }): string => `第 ${r.shard + 1} / ${r.shard_count} 段`;

/** 未完成(第 x / y 段) for a stopped batch: x is the next segment it will write, 1-based. */
export const pendingLabel = (b: { next_shard: number; shard_count: number }): string =>
  `未完成(第 ${Math.min(b.next_shard + 1, Math.max(b.shard_count, 1))} / ${b.shard_count} 段)`;

/**
 * The progress line while a batch runs. `last` is the shard just written, so while `busy` the shard
 * being written is the NEXT one: 「寫入中 第 2 / 3 段(已完成 1 段)」; idle: 「已寫入 1 / 3 段」.
 */
export function writingLabel(last: { shard: number; shard_count: number; done?: boolean }, busy: boolean): string {
  const completed = Math.min(last.shard + 1, last.shard_count);
  if (busy && !last.done && completed < last.shard_count) {
    return `寫入中 第 ${completed + 1} / ${last.shard_count} 段(已完成 ${completed} 段)`;
  }
  return `已寫入 ${completed} / ${last.shard_count} 段`;
}

/** Skip reason → the bucket the result line names. Every skip lands in exactly one bucket. */
export function skipBucket(reason: string): string {
  switch (reason) {
    case 'same': return '已存在且相同';
    case 'revoked_not_selected': return '已撤銷未勾選';
    case 'duplicate': return '檔內重複';
    case 'appeared_concurrently': return '寫入時已被加入';
    default: return '格式錯誤';
  }
}

const BUCKET_ORDER = ['已存在且相同', '已撤銷未勾選', '格式錯誤', '檔內重複', '寫入時已被加入'];

/** 「(已存在且相同 1、已撤銷未勾選 2、格式錯誤 1、檔內重複 1)」 — sums to the skipped rows given. */
export function skipBreakdown(skipped: readonly { reason: string }[]): string {
  if (skipped.length === 0) return '';
  const n = new Map<string, number>();
  for (const r of skipped) n.set(skipBucket(r.reason), (n.get(skipBucket(r.reason)) ?? 0) + 1);
  return `(${BUCKET_ORDER.filter((b) => n.get(b)).map((b) => `${b} ${n.get(b)}`).join('、')})`;
}
